import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { BrutalButton } from "@/components/ui/BrutalButton";

interface CookieTableRow {
  name: string;
  provider: string;
  duration: string;
  purpose: string;
}

function CookieTable({ cookies, t }: { cookies: CookieTableRow[]; t: (key: string) => string }) {
  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-sm border-2 border-dark shadow-brutal">
        <thead className="bg-muted">
          <tr>
            <th className="text-left p-3 border-b-2 border-dark font-semibold">{t('legal.cookies.types.essential.table.name')}</th>
            <th className="text-left p-3 border-b-2 border-dark font-semibold">{t('legal.cookies.types.essential.table.provider')}</th>
            <th className="text-left p-3 border-b-2 border-dark font-semibold">{t('legal.cookies.types.essential.table.duration')}</th>
            <th className="text-left p-3 border-b-2 border-dark font-semibold">{t('legal.cookies.types.essential.table.purpose')}</th>
          </tr>
        </thead>
        <tbody>
          {cookies.map((cookie, index) => (
            <tr key={index} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/50'}>
              <td className="p-3 border-b border-dark font-mono text-xs">{cookie.name}</td>
              <td className="p-3 border-b border-dark">{cookie.provider}</td>
              <td className="p-3 border-b border-dark">{cookie.duration}</td>
              <td className="p-3 border-b border-dark">{cookie.purpose}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CookiePolicy() {
  const { t } = useTranslation();

  const handleOpenCookieSettings = () => {
    window.dispatchEvent(new Event('openCookieSettings'));
  };

  const essentialCookies: CookieTableRow[] = [
    {
      name: t('legal.cookies.types.essential.cookies.cookieConsent.name'),
      provider: t('legal.cookies.types.essential.cookies.cookieConsent.provider'),
      duration: t('legal.cookies.types.essential.cookies.cookieConsent.duration'),
      purpose: t('legal.cookies.types.essential.cookies.cookieConsent.purpose'),
    },
    {
      name: t('legal.cookies.types.essential.cookies.authToken.name'),
      provider: t('legal.cookies.types.essential.cookies.authToken.provider'),
      duration: t('legal.cookies.types.essential.cookies.authToken.duration'),
      purpose: t('legal.cookies.types.essential.cookies.authToken.purpose'),
    },
  ];

  const analyticsCookies: CookieTableRow[] = [
    {
      name: t('legal.cookies.types.analytics.cookies.ga.name'),
      provider: t('legal.cookies.types.analytics.cookies.ga.provider'),
      duration: t('legal.cookies.types.analytics.cookies.ga.duration'),
      purpose: t('legal.cookies.types.analytics.cookies.ga.purpose'),
    },
    {
      name: t('legal.cookies.types.analytics.cookies.gaId.name'),
      provider: t('legal.cookies.types.analytics.cookies.gaId.provider'),
      duration: t('legal.cookies.types.analytics.cookies.gaId.duration'),
      purpose: t('legal.cookies.types.analytics.cookies.gaId.purpose'),
    },
    {
      name: t('legal.cookies.types.analytics.cookies.gid.name'),
      provider: t('legal.cookies.types.analytics.cookies.gid.provider'),
      duration: t('legal.cookies.types.analytics.cookies.gid.duration'),
      purpose: t('legal.cookies.types.analytics.cookies.gid.purpose'),
    },
  ];

  const marketingCookies: CookieTableRow[] = [
    {
      name: t('legal.cookies.types.marketing.cookies.fbp.name'),
      provider: t('legal.cookies.types.marketing.cookies.fbp.provider'),
      duration: t('legal.cookies.types.marketing.cookies.fbp.duration'),
      purpose: t('legal.cookies.types.marketing.cookies.fbp.purpose'),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('legal.cookies.title')}
        description={t('legal.cookies.intro')}
      />
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <BrutalCard className="p-8 max-w-4xl mx-auto">
          <SEOBreadcrumb items={[{ label: t('legal.cookies.breadcrumb') }]} align="left" />
          <h1 className="text-4xl font-heading font-bold mb-8">
            {t('legal.cookies.title')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('legal.cookies.lastUpdated')}
          </p>
          <p className="mb-8">{t('legal.cookies.intro')}</p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            {/* 1. Titolare del Trattamento */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                1. {t('legal.cookies.controller.title')}
              </h2>
              <p>{t('legal.cookies.controller.text')}</p>
              <div className="bg-muted/50 border-2 border-dark p-5 mt-4 shadow-brutal space-y-1">
                <p className="!mt-0"><strong>{t('legal.cookies.controller.name')}</strong></p>
                <p>{t('legal.cookies.controller.vat')}</p>
                <p>Email: <a href={`mailto:${t('legal.cookies.controller.email')}`} className="text-primary underline">{t('legal.cookies.controller.email')}</a></p>
              </div>
            </section>

            {/* 2. Cosa sono i Cookie */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                2. {t('legal.cookies.what.title')}
              </h2>
              <p>{t('legal.cookies.what.text')}</p>
            </section>

            {/* 3. Google Consent Mode v2 */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                3. {t('legal.cookies.consentMode.title')}
              </h2>
              <p>{t('legal.cookies.consentMode.text')}</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li dangerouslySetInnerHTML={{ __html: t('legal.cookies.consentMode.signals.analytics') }} />
                <li dangerouslySetInnerHTML={{ __html: t('legal.cookies.consentMode.signals.ads') }} />
                <li dangerouslySetInnerHTML={{ __html: t('legal.cookies.consentMode.signals.userData') }} />
                <li dangerouslySetInnerHTML={{ __html: t('legal.cookies.consentMode.signals.personalization') }} />
              </ul>
              <p className="mt-4 bg-muted/50 border-2 border-dark p-4 shadow-brutal">{t('legal.cookies.consentMode.default')}</p>
            </section>

            {/* 4. Cookie Utilizzati */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                4. {t('legal.cookies.types.title')}
              </h2>
              
              {/* 4.1 Essential */}
              <h3 className="text-xl font-heading font-semibold mb-2 mt-6">
                4.1 {t('legal.cookies.types.essential.title')}
              </h3>
              <p>{t('legal.cookies.types.essential.text')}</p>
              <CookieTable cookies={essentialCookies} t={t} />

              {/* 4.2 Analytics */}
              <h3 className="text-xl font-heading font-semibold mb-2 mt-6">
                4.2 {t('legal.cookies.types.analytics.title')}
              </h3>
              <p>{t('legal.cookies.types.analytics.text')}</p>
              <CookieTable cookies={analyticsCookies} t={t} />

              {/* 4.3 Marketing */}
              <h3 className="text-xl font-heading font-semibold mb-2 mt-6">
                4.3 {t('legal.cookies.types.marketing.title')}
              </h3>
              <p>{t('legal.cookies.types.marketing.text')}</p>
              <CookieTable cookies={marketingCookies} t={t} />
            </section>

            {/* 5. Cookie di Terze Parti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                5. {t('legal.cookies.thirdParty.title')}
              </h2>
              <p>{t('legal.cookies.thirdParty.text')}</p>
              <ul className="list-none space-y-4 mt-4">
                <li className="bg-muted/50 border-2 border-dark p-5 shadow-brutal">
                  <strong>{t('legal.cookies.thirdParty.google.name')}</strong>
                  <p className="mt-1">{t('legal.cookies.thirdParty.google.text')}</p>
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">
                    {t('legal.cookies.thirdParty.google.link')} →
                  </a>
                </li>
                <li className="bg-muted/50 border-2 border-dark p-5 shadow-brutal">
                  <strong>{t('legal.cookies.thirdParty.meta.name')}</strong>
                  <p className="mt-1">{t('legal.cookies.thirdParty.meta.text')}</p>
                  <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">
                    {t('legal.cookies.thirdParty.meta.link')} →
                  </a>
                </li>
                <li className="bg-muted/50 border-2 border-dark p-5 shadow-brutal">
                  <strong>{t('legal.cookies.thirdParty.stripe.name')}</strong>
                  <p className="mt-1">{t('legal.cookies.thirdParty.stripe.text')}</p>
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">
                    {t('legal.cookies.thirdParty.stripe.link')} →
                  </a>
                </li>
              </ul>
            </section>

            {/* 6. Base Giuridica */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                6. {t('legal.cookies.legalBasis.title')}
              </h2>
              <p>{t('legal.cookies.legalBasis.text')}</p>
              <p className="mt-3" dangerouslySetInnerHTML={{ __html: t('legal.cookies.legalBasis.consent') }} />
            </section>

            {/* 7. Gestione dei Cookie */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                7. {t('legal.cookies.management.title')}
              </h2>
              <p>{t('legal.cookies.management.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2 mt-4">
                7.1 {t('legal.cookies.management.banner.title')}
              </h3>
              <p>{t('legal.cookies.management.banner.text')}</p>

              <h3 className="text-xl font-heading font-semibold mb-2 mt-4">
                7.2 {t('legal.cookies.management.changePreferences.title')}
              </h3>
              <p>{t('legal.cookies.management.changePreferences.text')}</p>
              <div className="mt-4">
                <BrutalButton onClick={handleOpenCookieSettings}>
                  {t('legal.cookies.management.changePreferences.button')}
                </BrutalButton>
              </div>

              <h3 className="text-xl font-heading font-semibold mb-2 mt-6">
                7.3 {t('legal.cookies.management.browser.title')}
              </h3>
              <p>{t('legal.cookies.management.browser.text')}</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>{t('legal.cookies.management.browser.chrome')}</li>
                <li>{t('legal.cookies.management.browser.firefox')}</li>
                <li>{t('legal.cookies.management.browser.safari')}</li>
                <li>{t('legal.cookies.management.browser.edge')}</li>
              </ul>

              <div className="mt-4 bg-destructive/10 border-2 border-dark p-4 shadow-brutal">
                <p className="text-foreground m-0">
                  <strong>⚠️</strong> {t('legal.cookies.management.note')}
                </p>
              </div>
            </section>

            {/* 8. Durata dei Cookie */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                8. {t('legal.cookies.duration.title')}
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>{t('legal.cookies.duration.session.name')}:</strong> {t('legal.cookies.duration.session.text')}
                </li>
                <li>
                  <strong>{t('legal.cookies.duration.persistent.name')}:</strong> {t('legal.cookies.duration.persistent.text')}
                </li>
              </ul>
            </section>

            {/* 9. Trasferimenti Internazionali */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                9. {t('legal.cookies.transfers.title')}
              </h2>
              <p dangerouslySetInnerHTML={{ __html: t('legal.cookies.transfers.text') }} />
            </section>

            {/* 10. I Tuoi Diritti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                10. {t('legal.cookies.rights.title')}
              </h2>
              <p>{t('legal.cookies.rights.text')}</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>{t('legal.cookies.rights.list.withdraw')}</li>
                <li>{t('legal.cookies.rights.list.access')}</li>
                <li>{t('legal.cookies.rights.list.delete')}</li>
                <li>{t('legal.cookies.rights.list.object')}</li>
              </ul>
              <p className="mt-4" dangerouslySetInnerHTML={{ __html: t('legal.cookies.rights.exercise') }} />
            </section>

            {/* 11. Aggiornamenti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                11. {t('legal.cookies.updates.title')}
              </h2>
              <p>{t('legal.cookies.updates.text')}</p>
            </section>

            {/* 12. Contatti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                12. {t('legal.cookies.contact.title')}
              </h2>
              <p>{t('legal.cookies.contact.text')}</p>
              <p className="mt-2">
                <a href={`mailto:${t('legal.cookies.controller.email')}`} className="text-primary underline font-semibold">
                  {t('legal.cookies.contact.email')}
                </a>
              </p>
            </section>

            {/* Link Utili */}
            <section className="bg-muted/50 border-2 border-dark p-6 shadow-brutal mt-8">
              <h3 className="text-xl font-heading font-semibold mt-0 mb-0">
                {t('legal.cookies.usefulLinks')}
              </h3>
              <ul className="list-none space-y-2 mt-3 mb-0">
                <li>
                  <a href="/privacy" className="text-primary underline">
                    {t('legal.privacy.title')}
                  </a>
                </li>
                <li>
                  <a href="/terms" className="text-primary underline">
                    {t('legal.terms.title')}
                  </a>
                </li>
                <li>
                  <a href="https://www.garanteprivacy.it/" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    {t('legal.cookies.garantePrivacy')}
                  </a>
                </li>
              </ul>
            </section>
          </div>
        </BrutalCard>
      </main>

      <Footer />
    </div>
  );
}
